import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '@kss/api';
import { Alert, Button, Card, useToast } from '@kss/ui';
import { ArrowLeft, Printer, X } from 'lucide-react';
import { EntityPicker } from './EntityPicker';
import { postStockMovements, resolveDefaultWarehouseId } from '../../utils/inventory';
import { postLinesSequential, createHeaderAndGetId } from '../../utils/postLines';
import { printInvoiceByFormat, type InvoicePrintInput } from '../../print/templates';
import { getAssignedFormat } from '../../print/formats';
import { getTenantHeaderInlined, getPrintBrandingInlined, printQrUrl } from '../../print/branding';

/**
 * Dedicated Credit Note / Debit Note form (issues: "Credit/Debit Note uses
 * the Sales-Order form"). A note references a Sales Invoice (or customer),
 * pulls its details + items, computes the credit/debit amount, and links
 * back via `relatedInvoiceId`. Credit returns stock IN; debit returns stock OUT.
 */

type Kind = 'credit' | 'debit' | 'return';

const CFG: Record<Kind, {
  title: string; basePath: string; numberField: string; dateField: string;
  invoiceFkField: string; grandField: string; lineBasePath: string; lineFk: string;
  numberPrefix: string; subTotalField: string; stockDir: 'In' | 'Out';
  sourceType: string; listPath: string;
  // The source document this note is raised against. Credit Note / Sales Return
  // reference a customer SALES invoice; a Debit Note is supplier-side so it
  // references a PURCHASE invoice — pointing it at sales invoices means the
  // supplier's bill can never be found ("doesn't fetch the order number").
  refBasePath: string; refLineBasePath: string; refLineFk: string;
  refNumberField: string; refSecondaryKey: string; refLabel: string;
}> = {
  credit: {
    title: 'Credit Note', basePath: '/sales/CreditNote', numberField: 'creditNoteNumber',
    dateField: 'issueDate', invoiceFkField: 'relatedInvoiceId', grandField: 'grandTotal',
    lineBasePath: '/sales/CreditNoteLine', lineFk: 'creditNoteId', numberPrefix: 'CN',
    subTotalField: 'subTotal', stockDir: 'In', sourceType: 'CreditNote', listPath: '/panel/credit-notes',
    refBasePath: '/sales/SalesInvoice', refLineBasePath: '/sales/SalesInvoiceLine', refLineFk: 'salesInvoiceId',
    refNumberField: 'invoiceNumber', refSecondaryKey: 'customerName', refLabel: 'Reference Sales Invoice',
  },
  debit: {
    title: 'Debit Note', basePath: '/sales/DebitNote', numberField: 'debitNoteNumber',
    dateField: 'issueDate', invoiceFkField: 'relatedInvoiceId', grandField: 'grandTotal',
    lineBasePath: '/sales/DebitNoteLine', lineFk: 'debitNoteId', numberPrefix: 'DN',
    subTotalField: 'amount', stockDir: 'Out', sourceType: 'DebitNote', listPath: '/panel/debit-notes',
    refBasePath: '/purchase/PurchaseInvoice', refLineBasePath: '/purchase/PurchaseInvoiceLine', refLineFk: 'purchaseInvoiceId',
    refNumberField: 'invoiceNumber', refSecondaryKey: 'supplierName', refLabel: 'Reference Purchase Invoice',
  },
  return: {
    title: 'Sales Return', basePath: '/sales/SalesReturn', numberField: 'returnNumber',
    dateField: 'returnDate', invoiceFkField: 'salesInvoiceId', grandField: 'refundAmount',
    lineBasePath: '/sales/SalesReturnLine', lineFk: 'salesReturnId', numberPrefix: 'SR',
    subTotalField: 'subTotal', stockDir: 'In', sourceType: 'SalesReturn', listPath: '/panel/sales-returns',
    refBasePath: '/sales/SalesInvoice', refLineBasePath: '/sales/SalesInvoiceLine', refLineFk: 'salesInvoiceId',
    refNumberField: 'invoiceNumber', refSecondaryKey: 'customerName', refLabel: 'Reference Sales Invoice',
  },
};

interface NoteLine {
  key: string; productId: number | string | null; productName: string;
  quantity: number; unitPrice: number; taxRate: number;
}

const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const rid = () => Math.random().toString(36).slice(2);
const pickList = (d: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(d)) return d as Array<Record<string, unknown>>;
  const o = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } } | null;
  if (Array.isArray(o?.items)) return o!.items as Array<Record<string, unknown>>;
  if (Array.isArray(o?.data)) return o!.data as Array<Record<string, unknown>>;
  if (o?.data && Array.isArray((o.data as { items?: unknown[] }).items)) return (o.data as { items: unknown[] }).items as Array<Record<string, unknown>>;
  return [];
};

export default function CreditDebitNoteComposer({ kind }: { kind: Kind }) {
  const cfg = CFG[kind];
  // A Debit Note is raised against a SUPPLIER (purchase-side); Credit Note /
  // Sales Return are customer-side. The `customerId`/`customerName` state holds
  // the chosen party either way; on save we route it to supplierId for debit.
  const isDebit = kind === 'debit';
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';
  const navigate = useNavigate();
  const toast = useToast();

  const [noteNumber, setNoteNumber] = useState(isEdit ? '' : `${cfg.numberPrefix}-${Date.now().toString(36).toUpperCase().slice(-6)}`);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [customerId, setCustomerId] = useState<number | string | null>(null);
  const [customerName, setCustomerName] = useState('');
  const [relatedInvoiceId, setRelatedInvoiceId] = useState<number | string | null>(null);
  const [invoiceLabel, setInvoiceLabel] = useState('');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<NoteLine[]>([]);
  const [taxAmount, setTaxAmount] = useState(0);
  const [editRowId, setEditRowId] = useState<number | string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Referenced sales-invoice preview modal.
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [refInvoice, setRefInvoice] = useState<Record<string, unknown> | null>(null);
  const [refInvoiceLines, setRefInvoiceLines] = useState<Array<Record<string, unknown>>>([]);
  const [refLoading, setRefLoading] = useState(false);

  const subTotal = useMemo(() => round2(lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0)), [lines]);
  const computedTax = useMemo(() => round2(lines.reduce((s, l) => s + l.quantity * l.unitPrice * (l.taxRate / 100), 0)), [lines]);
  const grandTotal = useMemo(() => round2(subTotal + (taxAmount || computedTax)), [subTotal, taxAmount, computedTax]);

  // ---- Edit hydrate ----
  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = pickList((await apiClient.get(cfg.basePath)).data);
        const row = rows.find((x) => String(x.code ?? '') === String(id) || String(x.id ?? '') === String(id));
        if (!row || cancelled) return;
        setEditRowId((row.id ?? null) as number | string | null);
        setNoteNumber(String(row[cfg.numberField] ?? row.code ?? id));
        const dv = row[cfg.dateField]; if (dv) setDate(String(dv).slice(0, 10));
        // Debit notes store the party in supplierId/supplierName; others in customer*.
        const pid = isDebit ? (row.supplierId ?? row.customerId) : row.customerId;
        if (pid != null) setCustomerId(pid as number);
        setCustomerName(String((isDebit ? (row.supplierName ?? row.customerName) : row.customerName) ?? ''));
        const fk = row[cfg.invoiceFkField]; if (fk != null) { setRelatedInvoiceId(fk as number); setInvoiceLabel(`#${fk}`); }
        setReason(String(row.reason ?? ''));
        setTaxAmount(Number(row.taxAmount ?? 0));
        const lrows = pickList((await apiClient.get(cfg.lineBasePath)).data)
          .filter((x) => String(x[cfg.lineFk]) === String(row.id))
          .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
        if (lrows.length && !cancelled) {
          setLines(lrows.map((l) => ({
            key: rid(), productId: (l.productId ?? null) as number | null,
            productName: String(l.productName ?? l.name ?? ''),
            quantity: Number(l.quantity ?? 1) || 1, unitPrice: Number(l.unitPrice ?? 0), taxRate: Number(l.taxRate ?? 0),
          })));
        }
      } catch { /* blank */ }
    })();
    return () => { cancelled = true; };
  }, [isEdit, id, cfg.basePath, cfg.lineBasePath, cfg.lineFk, cfg.numberField]);

  // ---- Pick a Sales Invoice → fill customer + copy lines ----
  async function pickInvoice(inv: Record<string, unknown> | null) {
    if (!inv) { setRelatedInvoiceId(null); setInvoiceLabel(''); setRefInvoice(null); return; }
    const invId = (inv.id ?? null) as number | null;
    setRelatedInvoiceId(invId);
    setRefInvoice(inv);
    setInvoiceLabel(String(inv.invoiceNumber ?? inv.code ?? `#${invId}`));
    // Debit note pulls the SUPPLIER off a purchase invoice; credit/return pull
    // the CUSTOMER off a sales invoice.
    const pid = isDebit ? (inv.supplierId ?? inv.customerId) : inv.customerId;
    const pname = isDebit ? (inv.supplierName ?? inv.customerName) : inv.customerName;
    if (pid != null) setCustomerId(pid as number);
    if (pname) setCustomerName(String(pname));
    setTaxAmount(Number(inv.taxAmount ?? 0));
    try {
      let mapped: NoteLine[] = pickList((await apiClient.get(cfg.refLineBasePath)).data)
        .filter((x) => String(x[cfg.refLineFk]) === String(invId))
        .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0))
        .map((l) => ({
          key: rid(), productId: (l.productId ?? null) as number | null,
          productName: String(l.productName ?? l.name ?? ''),
          quantity: Number(l.quantity ?? 1) || 1, unitPrice: Number(l.unitPrice ?? 0), taxRate: Number(l.taxRate ?? 0),
        }));
      // Fallback: POS / dine-in invoices keep their items in the header
      // `description` JSON (no separate SalesInvoiceLine rows). Recover them
      // so the credit/return note still shows the items to adjust.
      if (mapped.length === 0) {
        try {
          const env = JSON.parse(String(inv.description ?? '{}')) as { lines?: Array<Record<string, unknown>>; items?: Array<Record<string, unknown>> };
          const src = (Array.isArray(env.lines) && env.lines.length ? env.lines : (Array.isArray(env.items) ? env.items : []));
          mapped = src.map((l) => ({
            key: rid(), productId: (l.productId ?? null) as number | null,
            productName: String(l.name ?? l.productName ?? l.itemName ?? ''),
            quantity: Number(l.quantity ?? l.qty ?? 1) || 1,
            unitPrice: Number(l.unitPrice ?? l.rate ?? l.price ?? 0),
            taxRate: Number(l.taxRate ?? 0),
          }));
        } catch { /* no description envelope */ }
      }
      setLines(mapped);
      toast.success('Loaded', `Pulled ${mapped.length} item(s) from ${String(inv.invoiceNumber ?? inv.code ?? '')}`);
    } catch { toast.error('Line copy failed', 'Loaded the invoice header only.'); }
  }

  function setLineQty(key: string, qty: number) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, quantity: Math.max(0, qty) } : l)));
  }

  /** Open the referenced sales-invoice in a modal — fetch fresh so it works
   *  in edit mode (where we only have the id) as well as right after picking. */
  async function openInvoiceModal() {
    if (!relatedInvoiceId) { toast.error('No invoice', 'Pick a reference sales invoice first.'); return; }
    setInvoiceModalOpen(true);
    setRefLoading(true);
    try {
      const invs = pickList((await apiClient.get(cfg.refBasePath)).data);
      const inv = invs.find((x) => String(x.id) === String(relatedInvoiceId)) ?? refInvoice;
      let ilines = pickList((await apiClient.get(cfg.refLineBasePath)).data)
        .filter((x) => String(x[cfg.refLineFk]) === String(relatedInvoiceId))
        .sort((a, b) => Number(a.lineNumber ?? 0) - Number(b.lineNumber ?? 0));
      // Fallback to the header `description` JSON for POS/dine-in invoices
      // that have no separate line rows.
      if (ilines.length === 0 && inv) {
        try {
          const env = JSON.parse(String(inv.description ?? '{}')) as { lines?: Array<Record<string, unknown>>; items?: Array<Record<string, unknown>> };
          const src = (Array.isArray(env.lines) && env.lines.length ? env.lines : (Array.isArray(env.items) ? env.items : []));
          ilines = src.map((l) => ({
            productName: String(l.name ?? l.productName ?? l.itemName ?? ''),
            quantity: Number(l.quantity ?? l.qty ?? 1) || 1,
            unitPrice: Number(l.unitPrice ?? l.rate ?? l.price ?? 0),
            taxRate: Number(l.taxRate ?? 0),
          }));
        } catch { /* no envelope */ }
      }
      setRefInvoice(inv);
      setRefInvoiceLines(ilines);
    } catch { toast.error('Load failed', 'Could not load the invoice.'); }
    finally { setRefLoading(false); }
  }

  /** Print the referenced sales invoice using the admin-assigned format. */
  async function printRefInvoice() {
    const r = refInvoice;
    if (!r) return;
    const taxAmt = Number(r.taxAmount ?? 0);
    const taxable = Number(r.subTotal ?? 0)
      || refInvoiceLines.reduce((s, l) => s + Number(l.quantity ?? 0) * Number(l.unitPrice ?? 0), 0);
    // Inline uploaded logo/signature/QR (auth-only URLs) → data URIs for print.
    const b = await getPrintBrandingInlined();
    const tenantHdr = await getTenantHeaderInlined();
    const payload: InvoicePrintInput = {
      docType: 'TAX INVOICE',
      number: String(r.invoiceNumber ?? r.code ?? relatedInvoiceId),
      date: String(r.invoiceDate ?? new Date().toISOString()),
      customerType: (isDebit ? r.supplierGstin : r.customerGstin) ? 'B2B' : 'B2C',
      tenant: tenantHdr,
      parties: [{ label: isDebit ? 'Supplier' : 'Bill to', name: String((isDebit ? r.supplierName : r.customerName) ?? ''), gstin: (isDebit ? r.supplierGstin : r.customerGstin) ? String(isDebit ? r.supplierGstin : r.customerGstin) : undefined }],
      lines: refInvoiceLines.map((l) => ({
        description: String(l.productName ?? l.name ?? '—'),
        hsnCode: l.hsnCode ? String(l.hsnCode) : undefined,
        qty: Number(l.quantity ?? 0), rate: Number(l.unitPrice ?? 0), taxRate: Number(l.taxRate ?? 0),
        total: Number(l.lineTotal ?? (Number(l.quantity ?? 0) * Number(l.unitPrice ?? 0))),
      })),
      subTotal: round2(taxable), totalDiscount: 0, taxableAmount: round2(taxable),
      cgstAmount: round2(taxAmt / 2), sgstAmount: round2(taxAmt / 2), igstAmount: 0, roundOff: 0,
      totalAmount: Number(r.grandTotal ?? 0),
      bank: (b.bankName || b.bankAccountNo) ? { name: b.bankName, accountName: b.bankAccountName, accountNo: b.bankAccountNo, ifsc: b.bankIfsc, branch: b.bankBranch } : undefined,
      signatureUrl: b.signatureUrl,
      qrUrl: printQrUrl(b.qrUrl, ['TAX INVOICE', String(r.invoiceNumber ?? r.code ?? ''), String(r.customerName ?? ''), 'Amt:' + Number(r.grandTotal ?? 0), tenantHdr.gstin]),
    };
    printInvoiceByFormat(getAssignedFormat('salesInvoice'), payload);
  }

  async function save() {
    if (!customerId) { setErr(`Select a ${isDebit ? 'supplier' : 'customer'} or a reference invoice.`); return; }
    setSaving(true); setErr(null);
    try {
      const payload: Record<string, unknown> = {
        code: noteNumber, name: `${cfg.title} ${noteNumber}`, status: 0,
        [cfg.numberField]: noteNumber,
        [cfg.dateField]: new Date(date).toISOString(),
        // Debit Note → supplier columns; Credit Note / Return → customer columns.
        // For a debit note we ALSO mirror the party into customerId/customerName
        // so the registry grid (which renders a Customer Name column) isn't blank
        // — the live DebitNote row carries both pairs.
        ...(isDebit
          ? { supplierId: Number(customerId), supplierName: customerName, customerId: Number(customerId), customerName }
          : { customerId: Number(customerId), customerName }),
        [cfg.invoiceFkField]: relatedInvoiceId ? Number(relatedInvoiceId) : null,
        reason: reason || undefined, currency: 'INR',
        [cfg.subTotalField]: subTotal,
        taxAmount: round2(taxAmount || computedTax),
        [cfg.grandField]: grandTotal,
      };
      let savedId: number | string | undefined;
      if (isEdit) {
        const r = await apiClient.put(cfg.basePath, { ...payload, ...(editRowId != null ? { id: Number(editRowId) } : {}), code: noteNumber });
        savedId = ((r.data?.data ?? r.data) as Record<string, unknown>)?.id as number ?? (editRowId != null ? Number(editRowId) : undefined);
      } else {
        // Create POST returns 200 with an EMPTY body (no id) → recover by the
        // unique code, else the line block below is skipped and the note saves
        // with ZERO line rows.
        const { id } = await createHeaderAndGetId(cfg.basePath, payload, {
          matchField: 'code',
          matchValue: noteNumber,
        });
        savedId = id;
      }
      const real = lines.filter((l) => l.productId != null || l.productName.trim());
      if (savedId != null && real.length) {
        if (isEdit) {
          try {
            const ex = pickList((await apiClient.get(cfg.lineBasePath)).data).filter((x) => String(x[cfg.lineFk]) === String(savedId));
            await Promise.all(ex.map((x) => apiClient.delete(`${cfg.lineBasePath}/${x.id}`).catch(() => undefined)));
          } catch { /* best effort */ }
        }
        // Sequential (not Promise.all) so a parallel burst can't race the auth
        // refresh and silently drop lines. Line code keys off the recovered id
        // so it's globally unique (no 409 duplicate_code).
        await postLinesSequential(cfg.lineBasePath, real.map((l, i) => ({
          code: `${savedId}-L${i + 1}`, name: l.productName || `Line ${i + 1}`, status: 0,
          [cfg.lineFk]: savedId, lineNumber: i + 1, productId: l.productId ?? null, productName: l.productName,
          quantity: l.quantity, unitOfMeasure: 'PCS', unitPrice: l.unitPrice, lineDiscount: 0,
          taxRate: l.taxRate, taxAmount: round2(l.quantity * l.unitPrice * (l.taxRate / 100)),
          lineTotal: round2(l.quantity * l.unitPrice * (1 + l.taxRate / 100)),
        })));
        // Inventory: credit note returns goods IN, debit note sends OUT.
        const warehouseId = await resolveDefaultWarehouseId();
        await postStockMovements({
          direction: cfg.stockDir, sourceType: cfg.sourceType,
          sourceId: savedId, docNumber: noteNumber, warehouseId,
          lines: real.map((l) => ({ productId: l.productId, quantity: l.quantity, unitCost: l.unitPrice, unitOfMeasure: 'PCS' })),
        });
      }
      toast.success('Saved', `${cfg.title} ${noteNumber} ${isEdit ? 'updated' : 'created'}`);
      navigate(cfg.listPath);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg); toast.error('Save failed', msg);
    } finally { setSaving(false); }
  }

  const inputCls = 'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm outline-none focus:border-primary';
  return (
    <div className="-m-6 space-y-4 p-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="grid h-8 w-8 place-items-center rounded border border-border hover:border-primary" aria-label="Back">
          <ArrowLeft size={16} />
        </button>
        <h1 className="text-base font-semibold">{isEdit ? 'Edit' : 'New'} {cfg.title}</h1>
      </div>
      {err && <Alert variant="danger">{err}</Alert>}

      {/* Single form — header + items + totals in one card (matches the
          Sales Invoice composer, no longer three separate cards). */}
      <Card className="space-y-4 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={`${cfg.title} No`}><input className={inputCls} value={noteNumber} onChange={(e) => setNoteNumber(e.target.value)} /></Field>
          <Field label="Date"><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-content-secondary">{cfg.refLabel}</span>
              <button
                type="button"
                onClick={() => void openInvoiceModal()}
                disabled={!relatedInvoiceId}
                className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline disabled:cursor-not-allowed disabled:text-content-muted disabled:no-underline"
                title="View / print the referenced invoice"
              >
                <Printer size={11} /> View / Print
              </button>
            </div>
            <EntityPicker<Record<string, unknown>>
              basePath={cfg.refBasePath} idKey="id" labelKey={cfg.refNumberField} secondaryKey={cfg.refSecondaryKey}
              value={relatedInvoiceId}
              selectedRow={invoiceLabel ? ({ id: relatedInvoiceId, [cfg.refNumberField]: invoiceLabel } as Record<string, unknown>) : null}
              onChange={(inv) => void pickInvoice(inv)} placeholder="Select invoice"
            />
          </div>
          <Field label={isDebit ? 'Supplier' : 'Customer'}>
            <EntityPicker<Record<string, unknown>>
              basePath={isDebit ? '/purchase/Supplier' : '/crm/Customer'} idKey="id" labelKey="name" secondaryKey="code"
              value={customerId}
              selectedRow={customerName ? ({ id: customerId, name: customerName } as Record<string, unknown>) : null}
              onChange={(c) => { setCustomerId((c?.id ?? null) as number | null); setCustomerName(String(c?.name ?? '')); }}
              placeholder={isDebit ? 'Select supplier' : 'Select customer'}
            />
          </Field>
          <Field label="Reason"><input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={kind === 'credit' ? 'Return / price adjustment' : 'Additional charge / correction'} /></Field>
        </div>

        <div className="overflow-hidden rounded-md border border-border">
          <div className="border-b border-border bg-surface-card-hover/30 px-3 py-2 text-sm font-medium">Items {lines.length ? `(${lines.length})` : ''}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-card-hover/40 text-left text-[11px] text-content-muted">
                <tr><th className="px-3 py-2">#</th><th className="px-3 py-2">Product</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">GST%</th><th className="px-3 py-2 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-content-muted">Pick a reference invoice to pull its items (adjust qty for a partial note).</td></tr>
                ) : lines.map((l, i) => (
                  <tr key={l.key} className="border-t border-border">
                    <td className="px-3 py-1.5 text-content-muted">{i + 1}</td>
                    <td className="px-3 py-1.5">{l.productName || '—'}</td>
                    <td className="px-3 py-1.5 text-right">
                      <input type="number" className="h-7 w-16 rounded border border-border bg-surface-card px-1 text-right" value={l.quantity} onChange={(e) => setLineQty(l.key, Number(e.target.value) || 0)} />
                    </td>
                    <td className="px-3 py-1.5 text-right">₹{l.unitPrice.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right">{l.taxRate}%</td>
                    <td className="px-3 py-1.5 text-right">₹{round2(l.quantity * l.unitPrice).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ml-auto w-full max-w-xs space-y-2 border-t border-border pt-3">
          <div className="flex items-center justify-between text-sm"><span className="text-content-secondary">Sub Total</span><span>₹ {subTotal.toFixed(2)}</span></div>
          <div className="flex items-center justify-between text-sm"><span className="text-content-secondary">Tax</span>
            <input type="number" className="h-8 w-32 rounded border border-border bg-surface-card px-2 text-right" value={taxAmount || computedTax} onChange={(e) => setTaxAmount(Number(e.target.value) || 0)} />
          </div>
          <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-semibold">
            <span>{kind === 'return' ? 'Refund' : kind === 'credit' ? 'Credit' : 'Debit'} Amount</span><span>₹ {grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
        <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : `Save ${cfg.title}`}</Button>
      </div>

      {/* Referenced sales-invoice preview modal (with print). */}
      {invoiceModalOpen && (
        <Overlay onClose={() => setInvoiceModalOpen(false)}>
          <div className="flex max-h-[88vh] flex-col">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-base font-semibold">
                {cfg.refLabel.replace(/^Reference\s+/, '')} {refInvoice ? String(refInvoice.invoiceNumber ?? refInvoice.code ?? invoiceLabel) : invoiceLabel}
              </h3>
              <button onClick={() => setInvoiceModalOpen(false)} className="grid h-8 w-8 place-items-center rounded hover:bg-surface-card-hover" aria-label="Close"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {refLoading ? (
                <div className="py-10 text-center text-sm text-content-muted">Loading invoice…</div>
              ) : !refInvoice ? (
                <div className="py-10 text-center text-sm text-content-muted">Invoice not found.</div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <Kv label="Invoice No" value={String(refInvoice.invoiceNumber ?? refInvoice.code ?? '—')} />
                    <Kv label="Date" value={refInvoice.invoiceDate ? new Date(String(refInvoice.invoiceDate)).toLocaleDateString('en-IN') : '—'} />
                    <Kv label={isDebit ? 'Supplier' : 'Customer'} value={String((isDebit ? refInvoice.supplierName : refInvoice.customerName) ?? '—')} />
                    <Kv label="GSTIN" value={String((isDebit ? refInvoice.supplierGstin : refInvoice.customerGstin) ?? '—')} />
                  </div>
                  <div className="overflow-hidden rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-surface-card-hover/40 text-left text-[11px] text-content-muted">
                        <tr><th className="px-3 py-2">Product</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Amount</th></tr>
                      </thead>
                      <tbody>
                        {refInvoiceLines.length === 0 ? (
                          <tr><td colSpan={4} className="px-3 py-4 text-center text-content-muted">No line items found for this invoice.</td></tr>
                        ) : refInvoiceLines.map((l, i) => (
                          <tr key={i} className="border-t border-border">
                            <td className="px-3 py-1.5">{String(l.productName ?? l.name ?? '—')}</td>
                            <td className="px-3 py-1.5 text-right">{Number(l.quantity ?? 0)}</td>
                            <td className="px-3 py-1.5 text-right">₹{Number(l.unitPrice ?? 0).toFixed(2)}</td>
                            <td className="px-3 py-1.5 text-right">₹{(Number(l.quantity ?? 0) * Number(l.unitPrice ?? 0)).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="ml-auto w-full max-w-xs space-y-1">
                    <div className="flex justify-between"><span className="text-content-secondary">Sub Total</span><span>₹ {Number(refInvoice.subTotal ?? 0).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-content-secondary">Tax</span><span>₹ {Number(refInvoice.taxAmount ?? 0).toFixed(2)}</span></div>
                    <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Grand Total</span><span>₹ {Number(refInvoice.grandTotal ?? 0).toFixed(2)}</span></div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-border p-3">
              <Button variant="outline" onClick={() => setInvoiceModalOpen(false)}>Close</Button>
              <Button onClick={printRefInvoice} disabled={!refInvoice}><Printer className="h-4 w-4" /> Print invoice</Button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-medium text-content-secondary">{label}</span>{children}</label>;
}

function Kv({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-content-muted">{label}</div>
      <div className="font-medium text-content-primary">{value}</div>
    </div>
  );
}

/** Lightweight modal overlay for the referenced-invoice preview. */
function Overlay({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3" onClick={onClose}>
      <div className="w-full max-w-2xl overflow-hidden rounded-xl border border-border bg-surface-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
